"use client";

import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Plus, PencilSimple, Trash, Star, Warning, Info } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { computeTaxes, effectiveRate } from "@/lib/tax";
import {
  fetchTaxRates,
  addTaxRate,
  addTaxGroup,
  updateTax,
  deleteTax,
} from "@/state/taxRates/taxRate.slice";

const pct = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}%`;
const money = (n) => `GH₵ ${Number(n || 0).toFixed(2)}`;
const byOrder = (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.id - b.id;

// ============================================
// Shared form bits
// ============================================
const DefaultAndStatus = ({ isDefault, setIsDefault, status, setStatus, showStatus, disabled }) => (
  <div className="space-y-3 rounded-md border p-3">
    <div className="flex items-start justify-between gap-4">
      <div>
        <Label htmlFor="is_default">Use as default</Label>
        <p className="text-xs text-muted-foreground">
          Charged on every product that has no tax of its own.
        </p>
      </div>
      <Switch id="is_default" checked={isDefault} onCheckedChange={setIsDefault} disabled={disabled} />
    </div>
    {showStatus && (
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label htmlFor="status">Active</Label>
          <p className="text-xs text-muted-foreground">
            Switched off, this tax is not charged on any sale.
          </p>
        </div>
        <Switch id="status" checked={status} onCheckedChange={setStatus} disabled={disabled} />
      </div>
    )}
  </div>
);

const FormFooter = ({ onCancel, saving, canSubmit, label }) => (
  <DialogFooter className="pt-2">
    <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
      Cancel
    </Button>
    <Button type="submit" disabled={saving || !canSubmit}>
      {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {label}
    </Button>
  </DialogFooter>
);

// ============================================
// Single tax rate form
// ============================================
const RateForm = ({ entry, hasDefault, onSubmit, onCancel, saving }) => {
  const [name, setName] = useState(entry?.name || "");
  const [rate, setRate] = useState(entry ? String(entry.rate) : "");
  const [isCompound, setIsCompound] = useState(!!entry?.is_compound);
  const [sortOrder, setSortOrder] = useState(entry ? String(entry.sort_order ?? "") : "");
  const [description, setDescription] = useState(entry?.description || "");
  // The first tax a business adds is almost always the one they charge.
  const [isDefault, setIsDefault] = useState(entry ? !!entry.is_default : !hasDefault);
  const [status, setStatus] = useState(entry ? entry.status === 1 : true);

  const rateNum = Number(rate);
  const rateValid = rate !== "" && rateNum > 0 && rateNum <= 100;
  const canSubmit = name.trim() && rateValid;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      name: name.trim(),
      rate: rateNum,
      is_compound: isCompound ? 1 : 0,
      ...(sortOrder !== "" && { sort_order: parseInt(sortOrder) || 0 }),
      description: description.trim(),
      is_default: isDefault ? 1 : 0,
      ...(entry && { status: status ? 1 : 0 }),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor="tax_name">Name *</Label>
          <Input
            id="tax_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. VAT, NHIL, GETFund"
            autoFocus
            disabled={saving}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tax_rate">Rate (%) *</Label>
          <Input
            id="tax_rate"
            type="number"
            inputMode="decimal"
            step="0.001"
            min="0"
            max="100"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="15"
            disabled={saving}
            aria-invalid={rate !== "" && !rateValid}
          />
        </div>
      </div>
      {rate !== "" && !rateValid && (
        <p className="text-xs text-red-600 dark:text-red-400">Rate must be more than 0 and at most 100.</p>
      )}

      <div className="flex items-start justify-between gap-4 rounded-md border p-3">
        <div>
          <Label htmlFor="is_compound">Compound tax</Label>
          <p className="text-xs text-muted-foreground">
            Charged on the price <em>plus</em> the other taxes in its group — e.g. VAT in Ghana is
            charged on top of NHIL, GETFund and COVID.
          </p>
        </div>
        <Switch id="is_compound" checked={isCompound} onCheckedChange={setIsCompound} disabled={saving} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="sort_order">Receipt order</Label>
          <Input
            id="sort_order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            placeholder="Auto"
            disabled={saving}
          />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor="tax_description">Description</Label>
          <Input
            id="tax_description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            disabled={saving}
          />
        </div>
      </div>

      <DefaultAndStatus
        isDefault={isDefault}
        setIsDefault={setIsDefault}
        status={status}
        setStatus={setStatus}
        showStatus={!!entry}
        disabled={saving}
      />

      <FormFooter onCancel={onCancel} saving={saving} canSubmit={canSubmit} label={entry ? "Save changes" : "Add tax rate"} />
    </form>
  );
};

// ============================================
// Tax group form
// ============================================
const GroupForm = ({ entry, rates, hasDefault, onSubmit, onCancel, saving }) => {
  const [name, setName] = useState(entry?.name || "");
  const [selected, setSelected] = useState(() => new Set(entry?.tax_rate_ids || []));
  const [description, setDescription] = useState(entry?.description || "");
  const [isDefault, setIsDefault] = useState(entry ? !!entry.is_default : !hasDefault);
  const [status, setStatus] = useState(entry ? entry.status === 1 : true);

  const toggle = (id, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  const members = rates.filter((r) => selected.has(r.id)).sort(byOrder);
  const charged = members.filter((m) => m.status === 1);
  const preview = computeTaxes(100, charged);
  const canSubmit = name.trim() && selected.size >= 2;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      name: name.trim(),
      tax_rate_ids: [...selected],
      description: description.trim(),
      is_default: isDefault ? 1 : 0,
      ...(entry && { status: status ? 1 : 0 }),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="group_name">Name *</Label>
        <Input
          id="group_name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Ghana Standard (VAT + levies)"
          autoFocus
          disabled={saving}
        />
      </div>

      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Sub taxes * <span className="font-normal text-muted-foreground">(pick at least two)</span></legend>
        <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
          {rates.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">Add tax rates first.</p>
          )}
          {[...rates].sort(byOrder).map((r) => (
            <label
              key={r.id}
              htmlFor={`member-${r.id}`}
              className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                id={`member-${r.id}`}
                checked={selected.has(r.id)}
                onCheckedChange={(v) => toggle(r.id, !!v)}
                disabled={saving}
              />
              <span className="flex-1 font-medium">{r.name}</span>
              {r.is_compound ? <Badge variant="outline" className="text-[10px]">Compound</Badge> : null}
              {r.status !== 1 && <Badge variant="secondary" className="text-[10px]">Off</Badge>}
              <span className="w-16 text-right tabular-nums text-muted-foreground">{pct(r.rate)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {charged.length > 0 && (
        <div className="rounded-md bg-muted/50 p-3 text-sm" aria-live="polite">
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            On a {money(100)} sale
          </p>
          {preview.lines.map((l) => (
            <div key={l.tax_rate_id} className="flex justify-between tabular-nums">
              <span>
                {l.name} {pct(l.rate)}
                {l.is_compound ? <span className="text-muted-foreground"> on {money(100 + preview.lines.filter((x) => !x.is_compound).reduce((s, x) => s + x.amount, 0))}</span> : null}
              </span>
              <span>{money(l.amount)}</span>
            </div>
          ))}
          <div className="mt-1.5 flex justify-between border-t pt-1.5 font-semibold tabular-nums">
            <span>Tax ({pct(effectiveRate(charged))})</span>
            <span>{money(preview.total)}</span>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="group_description">Description</Label>
        <Input
          id="group_description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional"
          disabled={saving}
        />
      </div>

      <DefaultAndStatus
        isDefault={isDefault}
        setIsDefault={setIsDefault}
        status={status}
        setStatus={setStatus}
        showStatus={!!entry}
        disabled={saving}
      />

      <FormFooter onCancel={onCancel} saving={saving} canSubmit={canSubmit} label={entry ? "Save changes" : "Add tax group"} />
    </form>
  );
};

// ============================================
// Table bits
// ============================================
const NameCell = ({ entry }) => (
  <TableCell>
    <div className="flex items-center gap-2">
      <span className="font-medium">{entry.name}</span>
      {entry.is_default ? <Badge className="text-[10px]">Default</Badge> : null}
    </div>
    {entry.description && <p className="text-xs text-muted-foreground">{entry.description}</p>}
  </TableCell>
);

const StatusCell = ({ entry }) => (
  <TableCell>
    <Badge variant={entry.status === 1 ? "success" : "secondary"}>
      {entry.status === 1 ? "Active" : "Off"}
    </Badge>
  </TableCell>
);

const ActionsCell = ({ entry, onDefault, onEdit, onDelete, busy }) => (
  <TableCell className="text-right whitespace-nowrap">
    <Button
      variant="ghost"
      size="icon"
      onClick={() => onDefault(entry)}
      disabled={busy || (!entry.is_default && entry.status !== 1)}
      aria-label={entry.is_default ? `Stop using ${entry.name} as default` : `Use ${entry.name} as default`}
      title={entry.is_default ? "Default tax — click to unset" : "Use as default"}
    >
      <Star weight={entry.is_default ? "fill" : "regular"} className={`h-4 w-4 ${entry.is_default ? "text-amber-500" : ""}`} />
    </Button>
    <Button variant="ghost" size="icon" onClick={() => onEdit(entry)} disabled={busy} aria-label={`Edit ${entry.name}`}>
      <PencilSimple className="h-4 w-4" />
    </Button>
    <Button variant="ghost" size="icon" onClick={() => onDelete(entry)} disabled={busy} aria-label={`Delete ${entry.name}`}>
      <Trash className="h-4 w-4 text-red-500" />
    </Button>
  </TableCell>
);

const EmptyRow = ({ cols, children }) => (
  <TableRow>
    <TableCell colSpan={cols} className="py-8 text-center text-sm text-muted-foreground">
      {children}
    </TableCell>
  </TableRow>
);

// ============================================
// Page
// ============================================
export default function TaxRatesPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { ready, rates, groups, businessRate, loaded, isLoading, isSaving, error } = useSelector(
    (state) => state.taxRate
  );

  const [dialog, setDialog] = useState(null); // { kind: 'rate' | 'group', entry? }
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchTaxRates());
  }, [dispatch]);

  const all = useMemo(() => [...rates, ...groups], [rates, groups]);
  const defaultEntry = all.find((e) => e.is_default && e.status === 1);
  const hasDefault = all.some((e) => e.is_default);
  const sortedRates = useMemo(() => [...rates].sort(byOrder), [rates]);

  const run = async (thunk, arg, successTitle) => {
    try {
      const res = await dispatch(thunk(arg)).unwrap();
      toast({ title: successTitle || res?.message || "Saved" });
      return true;
    } catch (message) {
      toast({ variant: "destructive", title: "Couldn't save", description: String(message) });
      return false;
    }
  };

  const save = async (payload) => {
    const { kind, entry } = dialog;
    const ok = entry
      ? await run(updateTax, { id: entry.id, ...payload })
      : await run(kind === "rate" ? addTaxRate : addTaxGroup, payload);
    if (ok) setDialog(null);
  };

  const toggleDefault = (entry) =>
    run(
      updateTax,
      { id: entry.id, is_default: entry.is_default ? 0 : 1 },
      entry.is_default ? `${entry.name} is no longer the default` : `${entry.name} is now the default tax`
    );

  const confirmDelete = async () => {
    const entry = toDelete;
    setToDelete(null);
    if (entry) await run(deleteTax, entry.id);
  };

  if (!loaded && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading tax rates…
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-5 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Tax Rates</h1>
          <p className="text-sm text-muted-foreground">
            Taxes charged on sales. Every product pays the default unless its own tax is set on the product form.
          </p>
        </div>
      </div>

      {error && !loaded && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {String(error)}
        </div>
      )}

      {loaded && !ready ? (
        <Card>
          <CardContent className="flex gap-3 p-5">
            <Warning className="h-5 w-5 shrink-0 text-amber-500" weight="fill" />
            <div className="space-y-1 text-sm">
              <p className="font-medium">Tax rates need a one-time database update</p>
              <p className="text-muted-foreground">
                Run <code className="rounded bg-muted px-1">server/migrations/create_tax_rates.sql</code> on the database, then
                reload this page. Until then, sales keep using the business tax rate ({pct(businessRate)}).
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Which tax an ordinary product pays right now */}
          <div
            className={`flex items-start gap-3 rounded-md border p-3 text-sm ${
              defaultEntry
                ? "bg-muted/40"
                : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
            }`}
          >
            {defaultEntry ? (
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <Warning className="mt-0.5 h-4 w-4 shrink-0" weight="fill" />
            )}
            <p>
              {defaultEntry ? (
                <>
                  Default tax: <strong>{defaultEntry.name}</strong> ({pct(defaultEntry.rate)}). Products without their own tax are charged this.
                </>
              ) : (
                <>
                  <strong>No default tax.</strong> Products without their own tax are charged{" "}
                  {businessRate > 0 ? `the business rate of ${pct(businessRate)}` : "no tax"}. Use the{" "}
                  <Star className="inline h-3.5 w-3.5 align-[-2px]" /> on a rate or group to make it the default.
                </>
              )}
            </p>
          </div>

          {/* Single rates */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
              <div>
                <CardTitle className="text-base">All your tax rates</CardTitle>
                <CardDescription>One tax each, e.g. VAT 15% or NHIL 2.5%.</CardDescription>
              </div>
              <Button size="sm" onClick={() => setDialog({ kind: "rate" })}>
                <Plus className="mr-1.5 h-4 w-4" /> Add
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Tax rate</TableHead>
                    <TableHead>Charged on</TableHead>
                    <TableHead className="text-right">Products</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRates.length === 0 ? (
                    <EmptyRow cols={6}>No tax rates yet. Add each tax you charge — VAT, NHIL, GETFund, COVID levy…</EmptyRow>
                  ) : (
                    sortedRates.map((r) => (
                      <TableRow key={r.id} className={r.status !== 1 ? "opacity-60" : ""}>
                        <NameCell entry={r} />
                        <TableCell className="text-right tabular-nums">{pct(r.rate)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.is_compound ? "Price + other taxes" : "Price"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.product_count || "—"}</TableCell>
                        <StatusCell entry={r} />
                        <ActionsCell
                          entry={r}
                          busy={isSaving}
                          onDefault={toggleDefault}
                          onEdit={(entry) => setDialog({ kind: "rate", entry })}
                          onDelete={setToDelete}
                        />
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Groups */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
              <div>
                <CardTitle className="text-base">Tax groups</CardTitle>
                <CardDescription>Combinations of taxes charged together, e.g. NHIL + GETFund + COVID + VAT.</CardDescription>
              </div>
              <Button
                size="sm"
                onClick={() => setDialog({ kind: "group" })}
                disabled={rates.length < 2}
                title={rates.length < 2 ? "Add at least two tax rates first" : undefined}
              >
                <Plus className="mr-1.5 h-4 w-4" /> Add
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Tax rate</TableHead>
                    <TableHead>Sub taxes</TableHead>
                    <TableHead className="text-right">Products</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.length === 0 ? (
                    <EmptyRow cols={6}>
                      {rates.length < 2
                        ? "Add at least two tax rates, then combine them here."
                        : "No groups yet. Group the taxes you charge together so products can pick them in one go."}
                    </EmptyRow>
                  ) : (
                    groups.map((g) => (
                      <TableRow key={g.id} className={g.status !== 1 ? "opacity-60" : ""}>
                        <NameCell entry={g} />
                        <TableCell className="text-right tabular-nums">{pct(g.rate)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {g.sub_taxes.map((s) => (
                              <Badge
                                key={s.id}
                                variant="outline"
                                className={`font-normal ${s.status !== 1 ? "line-through opacity-60" : ""}`}
                                title={s.status !== 1 ? `${s.name} is switched off and not charged` : undefined}
                              >
                                {s.name} {pct(s.rate)}
                                {s.is_compound ? "*" : ""}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{g.product_count || "—"}</TableCell>
                        <StatusCell entry={g} />
                        <ActionsCell
                          entry={g}
                          busy={isSaving}
                          onDefault={toggleDefault}
                          onEdit={(entry) => setDialog({ kind: "group", entry })}
                          onDelete={setToDelete}
                        />
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {groups.some((g) => g.sub_taxes.some((s) => s.is_compound)) && (
                <p className="border-t px-4 py-2 text-xs text-muted-foreground">
                  * Compound: charged on the price plus the group&apos;s other taxes.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!dialog} onOpenChange={(open) => !open && !isSaving && setDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialog?.entry ? "Edit" : "Add"} {dialog?.kind === "group" ? "tax group" : "tax rate"}
            </DialogTitle>
            <DialogDescription>
              {dialog?.kind === "group"
                ? "Pick the taxes that are always charged together."
                : "A single tax and the percentage it charges."}
            </DialogDescription>
          </DialogHeader>
          {dialog?.kind === "rate" && (
            <RateForm key={dialog.entry?.id || "new"} entry={dialog.entry} hasDefault={hasDefault} onSubmit={save} onCancel={() => setDialog(null)} saving={isSaving} />
          )}
          {dialog?.kind === "group" && (
            <GroupForm key={dialog.entry?.id || "new"} entry={dialog.entry} rates={rates} hasDefault={hasDefault} onSubmit={save} onCancel={() => setDialog(null)} saving={isSaving} />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Past sales keep the tax they were charged. A tax still used by products
              {toDelete && !toDelete.is_group ? " or groups" : ""} can&apos;t be deleted — switch it off instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
